import React, { useState, useCallback } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import EditIcon from '@mui/icons-material/Edit'

import Table from 'components/Table'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import BreadCrumbLink from 'components/BreadCrumbLink'
import HelpVideoLink from 'components/HelpVideoLink'

import CantRestoreModal from 'pages/shared/ArchivedDevices/CantRestoreModal'
import ArchiveDeviceModal from 'pages/shared/ArchivedDevices/ArchiveDeviceModal'

import { useSessionContext } from 'hooks/SessionContext'
import { useStoreContext } from 'hooks/StoreContext'
import useArchivedDevices from 'hooks/useArchivedDevices'

import { shortDateTime } from 'helpers/formatters'

import Api from 'services/api'

import { PageTitle, screenSize, LoadingSinglePanePage, SinglePanePage } from 'styles/shared'

import { ArchivedDevice } from 'types/ArchivedDevice'

import { color } from 'styles/colors'

type ArchiveDeviceModalState = {
  [index: string]: boolean
}

const ArchivedDevices = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())

  useArchivedDevices(refreshTimestamp)

  const { session: { dealerId } } = useSessionContext()
  const refreshCallback = useCallback(() => setRefreshTimestamp(moment().valueOf()), [])

  const [showCantRestoreModal, setShowCantRestoreModal] = useState(false)
  const [showArchiveDeviceModal, setShowArchiveDeviceModal] = useState<ArchiveDeviceModalState>({})

  const { archivedDevices, isArchivedDevicesLoaded } = useStoreContext()

  const onUpdateCallback = async (updatedArchiveDevice: ArchivedDevice) => {
    await Api.ArchivedDevices.update(
      updatedArchiveDevice.dealerId,
      updatedArchiveDevice.id,
      updatedArchiveDevice
    ).then(() => {
      refreshCallback()
    })
  }

  const showRestoreModal = (archivedDevice) => {
    if (!archivedDevice.registered) {
      setShowCantRestoreModal(true)
    } else {
      if (confirm('Are you sure you want to restore this device?')) {
        Api.ArchivedDevices.delete(dealerId, archivedDevice.id)
          .finally(() => {
            refreshCallback()
          })
      }
    }
  }

  if (!isArchivedDevicesLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <SinglePanePage>
      <S.Top>
        <BreadCrumbLink toRoute={'/customers/'} label={'CUSTOMERS'} direction={'back'} />
      </S.Top>

      <S.Header>
        <S.PageTitle>{archivedDevices.length} Archived Device{archivedDevices.length !== 1 ? 's' : ''}</S.PageTitle>
        <HelpVideoLink videoKey="archiving_devices" />
      </S.Header>

      <Table
        items={archivedDevices}
        noDataMessage={{
          title: 'No Archived devices',
          description: 'Devices that have been archived will show up here.'
        }}
      >
        <Table.Header>
          <Table.HeaderCell title="Device ID" />
          <Table.HeaderCell title="Reason" />
          <Table.HeaderCell title="Archived On" />
        </Table.Header>

        {archivedDevices.map(archivedDevice => (
          <div key={archivedDevice.id}>
            <Table.Row>
              <S.Cell>{archivedDevice.auid}</S.Cell>

              <S.Cell>
                <S.ReasonContainer>
                  <div>{archivedDevice.reason ? archivedDevice.reason : '-'}</div>
                  <div>
                    <S.EditIcon
                      fontSize="small"
                      color="primary"
                      onClick={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [archivedDevice.auid]: true }))}
                    />
                  </div>
                </S.ReasonContainer>
              </S.Cell>

              <S.ButtonCell>
                <span>
                  {shortDateTime(archivedDevice.createdAt)}
                </span>

                <S.ButtonContainer>
                  <Button decoration="secondary" text="Restore" onPress={() => showRestoreModal(archivedDevice)} />
                </S.ButtonContainer>

              </S.ButtonCell>
            </Table.Row>

            <ArchiveDeviceModal
              actionType="update"
              auid={archivedDevice.auid}
              reason={archivedDevice.reason}
              onUpdateCallback={(reason) => onUpdateCallback({ ...archivedDevice, reason })}
              isVisible={showArchiveDeviceModal[archivedDevice.auid]}
              closeCallback={() => setShowArchiveDeviceModal(showArchiveDeviceModal => ({ ...showArchiveDeviceModal, [archivedDevice.auid]: false }))}
            />
          </div>
        ))}

        <CantRestoreModal isVisible={showCantRestoreModal} closeCallback={() => setShowCantRestoreModal(false)} />
      </Table>
    </SinglePanePage>
  )
}

const CellStyles = `
  display: flex;
  line-height: 80px;
  padding-left: 10px;
  text-overflow: ellipsis;
  overflow: hidden;

  @media ${screenSize.medium} {
    line-height: 30px;
  }
`

const S = {
  Cell: styled(Table.Cell)`
    ${CellStyles}
  `,
  Header: styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: revert;

    @media ${screenSize.small} {
      display: block;
    }
  `,
  ReasonContainer: styled.div`
    cursor: pointer;
    justify-content: space-between;
    display: flex;
    width: 100%;
  `,
  ButtonContainer: styled.div`
    margin-right: 20px;
    margin-left: 10px;

    @media ${screenSize.medium} {
      margin-right: 0px;
    }
  `,
  ButtonCell: styled(Table.Cell)`
    ${CellStyles}
    justify-content: space-between;
    margin: auto;
    align-items: center;

    @media ${screenSize.medium} {
      margin: 0;
    }
  `,
  Top: styled.div`
    display: flex;
    justify-content: flex-start;
  `,
  EditIcon: styled(EditIcon)`
    color: ${color('EditIcon')};
  `
}

export default ArchivedDevices
